import React from 'react';
import { Eye, Edit, Trash2, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';

const QuotationTable = ({
                            quotations,
                            isLoading,
                            pagination,
                            onPaginationChange,
                            onView,
                            onEdit,
                            onDelete
                        }) => {
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            pending: { bg: '#fffbeb', color: '#f59e0b', text: 'Pending' },
            approved: { bg: '#ecfdf5', color: '#059669', text: 'Approved' },
            rejected: { bg: '#fef2f2', color: '#dc2626', text: 'Rejected' },
            expired: { bg: '#f3f4f6', color: '#6b7280', text: 'Expired' }
        };

        const config = statusConfig[status] || statusConfig.pending;

        return (
            <span style={{
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                backgroundColor: config.bg,
                color: config.color
            }}>
        {config.text}
      </span>
        );
    };

    const getValidityStatus = (createdAt, validityDays) => {
        const created = new Date(createdAt);
        const expiry = new Date(created.getTime() + (validityDays * 24 * 60 * 60 * 1000));
        const now = new Date();
        const daysLeft = Math.ceil((expiry - now) / (24 * 60 * 60 * 1000));

        if (daysLeft < 0) {
            return { text: 'Expired', color: '#dc2626' };
        } else if (daysLeft <= 3) {
            return { text: `${daysLeft} days left`, color: '#f59e0b' };
        } else {
            return { text: `${daysLeft} days left`, color: '#059669' };
        }
    };

    const totalPages = Math.ceil(pagination.total / pagination.limit);

    const handlePageChange = (newPage) => {
        onPaginationChange(prev => ({ ...prev, page: newPage }));
    };

    if (isLoading) {
        return (
            <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '48px',
                textAlign: 'center',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb'
            }}>
                <LoadingSpinner />
                <p style={{ marginTop: '16px', color: '#6b7280' }}>Loading quotations...</p>
            </div>
        );
    }

    if (quotations.length === 0) {
        return (
            <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '48px',
                textAlign: 'center',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb'
            }}>
                <div style={{
                    width: '64px',
                    height: '64px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px'
                }}>
                    📄
                </div>
                <h3 style={{ margin: '0 0 8px 0', color: '#1f2937' }}>No quotations found</h3>
                <p style={{ margin: 0, color: '#6b7280' }}>
                    Get started by creating your first quotation
                </p>
            </div>
        );
    }

    return (
        <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb',
            overflow: 'hidden'
        }}>
            {/* Table */}
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                    <tr style={{ backgroundColor: '#f9fafb' }}>
                        <th style={{
                            padding: '16px',
                            textAlign: 'left',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#374151',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            borderBottom: '1px solid #e5e7eb'
                        }}>
                            Quotation #
                        </th>
                        <th style={{
                            padding: '16px',
                            textAlign: 'left',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#374151',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            borderBottom: '1px solid #e5e7eb'
                        }}>
                            Client
                        </th>
                        <th style={{
                            padding: '16px',
                            textAlign: 'left',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#374151',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            borderBottom: '1px solid #e5e7eb'
                        }}>
                            Project
                        </th>
                        <th style={{
                            padding: '16px',
                            textAlign: 'left',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#374151',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            borderBottom: '1px solid #e5e7eb'
                        }}>
                            Status
                        </th>
                        <th style={{
                            padding: '16px',
                            textAlign: 'right',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#374151',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            borderBottom: '1px solid #e5e7eb'
                        }}>
                            Amount
                        </th>
                        <th style={{
                            padding: '16px',
                            textAlign: 'left',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#374151',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            borderBottom: '1px solid #e5e7eb'
                        }}>
                            Validity
                        </th>
                        <th style={{
                            padding: '16px',
                            textAlign: 'left',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#374151',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            borderBottom: '1px solid #e5e7eb'
                        }}>
                            Created
                        </th>
                        <th style={{
                            padding: '16px',
                            textAlign: 'center',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#374151',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            borderBottom: '1px solid #e5e7eb'
                        }}>
                            Actions
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    {quotations.map((quotation, index) => {
                        const validity = getValidityStatus(quotation.createdAt, quotation.validityDays);

                        return (
                            <tr
                                key={quotation.id}
                                style={{
                                    borderBottom: index < quotations.length - 1 ? '1px solid #f3f4f6' : 'none',
                                    transition: 'background-color 0.2s ease'
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f9fafb';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                <td style={{ padding: '16px' }}>
                    <span style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1e40af'
                    }}>
                      {quotation.quotationNumber}
                    </span>
                                </td>
                                <td style={{ padding: '16px' }}>
                                    <div>
                                        <div style={{
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: '#1f2937',
                                            marginBottom: '2px'
                                        }}>
                                            {quotation.clientName}
                                        </div>
                                        <div style={{
                                            fontSize: '12px',
                                            color: '#6b7280'
                                        }}>
                                            {quotation.clientEmail}
                                        </div>
                                    </div>
                                </td>
                                <td style={{ padding: '16px' }}>
                    <span style={{
                        fontSize: '14px',
                        color: '#1f2937'
                    }}>
                      {quotation.projectName}
                    </span>
                                </td>
                                <td style={{ padding: '16px' }}>
                                    {getStatusBadge(quotation.status)}
                                </td>
                                <td style={{ padding: '16px', textAlign: 'right' }}>
                    <span style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1f2937'
                    }}>
                      {formatCurrency(quotation.totalAmount)}
                    </span>
                                </td>
                                <td style={{ padding: '16px' }}>
                    <span style={{
                        fontSize: '12px',
                        color: validity.color,
                        fontWeight: '500'
                    }}>
                      {validity.text}
                    </span>
                                </td>
                                <td style={{ padding: '16px' }}>
                    <span style={{
                        fontSize: '14px',
                        color: '#6b7280'
                    }}>
                      {formatDate(quotation.createdAt)}
                    </span>
                                </td>
                                <td style={{ padding: '16px' }}>
                                    <div style={{
                                        display: 'flex',
                                        gap: '8px',
                                        justifyContent: 'center'
                                    }}>
                                        <button
                                            onClick={() => onView(quotation)}
                                            style={{
                                                padding: '6px',
                                                backgroundColor: '#eff6ff',
                                                color: '#1e40af',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseOver={(e) => {
                                                e.target.style.backgroundColor = '#dbeafe';
                                            }}
                                            onMouseOut={(e) => {
                                                e.target.style.backgroundColor = '#eff6ff';
                                            }}
                                            title="View"
                                        >
                                            <Eye size={16} />
                                        </button>
                                        <button
                                            onClick={() => onEdit(quotation)}
                                            style={{
                                                padding: '6px',
                                                backgroundColor: '#f0fdf4',
                                                color: '#059669',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseOver={(e) => {
                                                e.target.style.backgroundColor = '#dcfce7';
                                            }}
                                            onMouseOut={(e) => {
                                                e.target.style.backgroundColor = '#f0fdf4';
                                            }}
                                            title="Edit"
                                        >
                                            <Edit size={16} />
                                        </button>
                                        <button
                                            style={{
                                                padding: '6px',
                                                backgroundColor: '#fffbeb',
                                                color: '#f59e0b',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseOver={(e) => {
                                                e.target.style.backgroundColor = '#fef3c7';
                                            }}
                                            onMouseOut={(e) => {
                                                e.target.style.backgroundColor = '#fffbeb';
                                            }}
                                            title="Export"
                                        >
                                            <Download size={16} />
                                        </button>
                                        <button
                                            onClick={() => onDelete(quotation)}
                                            style={{
                                                padding: '6px',
                                                backgroundColor: '#fef2f2',
                                                color: '#dc2626',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseOver={(e) => {
                                                e.target.style.backgroundColor = '#fee2e2';
                                            }}
                                            onMouseOut={(e) => {
                                                e.target.style.backgroundColor = '#fef2f2';
                                            }}
                                            title="Delete"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div style={{
                    padding: '16px 24px',
                    borderTop: '1px solid #e5e7eb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div style={{
                        fontSize: '14px',
                        color: '#6b7280'
                    }}>
                        Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                        {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                        {pagination.total} results
                    </div>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <button
                            onClick={() => handlePageChange(pagination.page - 1)}
                            disabled={pagination.page === 1}
                            style={{
                                padding: '8px',
                                backgroundColor: pagination.page === 1 ? '#f9fafb' : 'white',
                                color: pagination.page === 1 ? '#9ca3af' : '#374151',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <ChevronLeft size={16} />
                        </button>

                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                            <button
                                key={page}
                                onClick={() => handlePageChange(page)}
                                style={{
                                    padding: '8px 12px',
                                    backgroundColor: page === pagination.page ? '#1e40af' : 'white',
                                    color: page === pagination.page ? 'white' : '#374151',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {page}
                            </button>
                        ))}

                        <button
                            onClick={() => handlePageChange(pagination.page + 1)}
                            disabled={pagination.page === totalPages}
                            style={{
                                padding: '8px',
                                backgroundColor: pagination.page === totalPages ? '#f9fafb' : 'white',
                                color: pagination.page === totalPages ? '#9ca3af' : '#374151',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                cursor: pagination.page === totalPages ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuotationTable;